import { FieldError } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { TagContainer, TagInput } from '@calendly/tags';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { useAllowFocus } from '@calendly/ui/hooks/use-allow-focus';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRecapShare } from 'features/recaps/meeting_list/meeting_card/recap_share_context';
import { PICKER_ENTER_RESULT_KIND, RECIPIENT_KINDS } from 'features/recaps/modals/share_modal/constants';
import { buildSuggestionExclusion, resolvePickerInputOnEnter } from 'features/recaps/modals/share_modal/utils';
import { RecipientChip } from './recipient_chip';
import { SuggestionsDropdown } from './suggestions_dropdown';
import { useRecipientSuggestions } from './use_recipient_suggestions';
const containerCss = "cry3cw4";
const tagContainerCss = "t1cmrpv5";
const StyledTagContainer = forwardRef((props, ref) => <TagContainer ref={ref} {...props} className={classNames(tagContainerCss, props.className)} />);
export const RecipientsPicker = ({
  autoFocus = false,
  disabled = false,
  showSearch
}) => {
  const {
    accessList: {
      internalUsers,
      userGroups,
      externalUsers,
      pendingInvites
    },
    hasExternalRecapSharing,
    pendingRecipients,
    permissionUpdates: {
      getUserRelation,
      getUserGroupRelation
    },
    recipientActions: {
      addRecipient,
      addEmailRecipient,
      removeRecipient
    },
    validation: {
      showInvalidEmailError,
      setShowInvalidEmailError
    },
    owner
  } = useRecapShare();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const {
    assignFocusRef
  } = useAllowFocus();
  const suggestionExclusion = useMemo(() => buildSuggestionExclusion({
    pendingRecipients,
    accessListUsers: internalUsers,
    accessListUserGroups: userGroups,
    accessListExternalUsers: externalUsers,
    accessListPendingInvites: pendingInvites,
    getUserRelation,
    getUserGroupRelation,
    ownerUserUuid: owner?.uuid
  }), [externalUsers, getUserGroupRelation, getUserRelation, internalUsers, owner?.uuid, pendingInvites, pendingRecipients, userGroups]);
  const {
    suggestions,
    isSearching,
    hasQuery
  } = useRecipientSuggestions(query, suggestionExclusion);

  // The dropdown anchors to the input but is portaled (via FixedContainer) so it
  // escapes the modal's `overflow: hidden`. Position is applied imperatively (the
  // `no-style-attribute` lint forbids dynamic inline styles).
  const positionDropdown = useCallback(() => {
    const container = containerRef.current;
    const dropdown = dropdownRef.current;
    if (!container || !dropdown) return;
    const rect = container.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom}px`;
    dropdown.style.left = `${rect.left}px`;
    dropdown.style.width = `${rect.width}px`;
  }, []);

  // `assignFocusRef` keeps the portaled dropdown interactive inside the modal
  // focus lock.
  const setDropdownNode = useCallback(node => {
    dropdownRef.current = node;
    assignFocusRef(node ?? null);
    if (node) positionDropdown();
  }, [assignFocusRef, positionDropdown]);
  useEffect(() => {
    if (autoFocus && showSearch) {
      inputRef.current?.focus();
    }
  }, [autoFocus, showSearch]);
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    positionDropdown();
    const raf = requestAnimationFrame(positionDropdown);
    window.addEventListener('resize', positionDropdown);
    window.addEventListener('scroll', positionDropdown, true);

    // Chip wrap/collapse changes container height without firing window resize;
    // keep the portaled dropdown anchored to the container bottom (NT-2987).
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(positionDropdown);
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', positionDropdown);
      window.removeEventListener('scroll', positionDropdown, true);
      resizeObserver?.disconnect();
    };
  }, [isOpen, positionDropdown]);
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleDocumentClick = event => {
      if (containerRef.current?.contains(event.target)) return;
      if (dropdownRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [isOpen]);
  if (!showSearch) {
    return null;
  }
  const handleSelect = suggestion => {
    addRecipient(suggestion);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleRemove = recipient => {
    removeRecipient(recipient.id);
  };
  const handleInputFocus = () => {
    setIsOpen(true);
    setShowInvalidEmailError(false);
  };
  const handleEnterKey = event => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    const result = resolvePickerInputOnEnter({
      query,
      suggestions,
      isSearching,
      hasExternalRecapSharing
    });
    if (!result) {
      return;
    }
    if (result.kind === PICKER_ENTER_RESULT_KIND.SUGGESTION) {
      handleSelect(result.suggestion);
      return;
    }
    const {
      recipient
    } = result;
    if (suggestionExclusion.excludedEmails.has(recipient.email)) {
      setQuery('');
      return;
    }
    addEmailRecipient(recipient);
    setQuery('');
    setShowInvalidEmailError(false);
    inputRef.current?.focus();
  };
  const placeholder = pendingRecipients.length ? '' : I18n.t('publisher.meeting_recap.share_modal.picker.placeholder');
  return <div ref={containerRef} className={containerCss} data-testid="recipients-picker">
      <StyledTagContainer ref={inputRef} inputValue={query} onInputChange={setQuery} onInputFocus={handleInputFocus} onKeyDown={handleEnterKey} onMouseDown={() => setIsOpen(true)} isDisabled={disabled} inputAttributes={{
      name: 'recipient_search',
      placeholder,
      'aria-label': I18n.t('publisher.meeting_recap.share_modal.picker.placeholder')
    }}>
        {pendingRecipients.map(recipient => <RecipientChip key={recipient.id} recipient={recipient} isInvalid={recipient.kind === RECIPIENT_KINDS.USER_EMAIL && recipient.isValidEmail === false} onRemove={() => handleRemove(recipient)} />)}
        <TagInput />
      </StyledTagContainer>
      {showInvalidEmailError && <FieldError text={I18n.t('publisher.meeting_intelligence.share.email_invalid_error')} id="recipients-picker-invalid-email" />}
      {isOpen && !disabled && <FixedContainer ref={setDropdownNode}>
          <SuggestionsDropdown suggestions={suggestions} isSearching={isSearching} hasQuery={hasQuery} onSelect={handleSelect} />
        </FixedContainer>}
    </div>;
};

require("./recipients_picker.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./recipients_picker.js");